import { Capacitor } from '@capacitor/core'
import { doc, getDoc, onSnapshot, type DocumentData, type Unsubscribe } from 'firebase/firestore'
import { db } from './firebase'
import type { DeviceInfo } from './deviceInfo'

export type NotificationLevel = 'noNotification' | 'softNotification' | 'hardNotification'

export interface VersionEntry {
  version: string
  level: NotificationLevel
}

export interface LatestVersions {
  web: VersionEntry | null
  ios: VersionEntry | null
  android: VersionEntry | null
}

export type VersionNotificationType = 'none' | 'web' | 'ios' | 'android' | 'both'

export interface VersionCheckEvaluation {
  shouldShow: boolean
  isDismissable: boolean
  notificationType: VersionNotificationType
  isHardNative: boolean
  currentWebVersion: string
  latestWebVersion: string | null
  currentNativeVersion: string | null
  latestNativeVersion: string | null
}

export function getVersioningDocRef() {
  return doc(db, 'dynamicData', 'versioning')
}

/** On native, read /version.json after reload so web version reflects deployed assets (not only build-time env). */
export async function withRuntimeWebVersion(deviceInfo: DeviceInfo): Promise<DeviceInfo> {
  if (!Capacitor.isNativePlatform()) {
    return deviceInfo
  }

  const runtimeVersion = await fetchRuntimeWebVersion()
  if (!runtimeVersion) {
    return deviceInfo
  }

  return { ...deviceInfo, webVersion: runtimeVersion }
}

async function fetchRuntimeWebVersion(): Promise<string | null> {
  try {
    const response = await fetch(`/version.json?gmt=${Date.now()}`, { cache: 'no-store' })
    if (!response.ok) return null

    const data = (await response.json()) as { extendedVersion?: string; version?: string }
    const version = data.extendedVersion ?? data.version
    return typeof version === 'string' && version.length > 0 ? version : null
  } catch {
    return null
  }
}

/** Parse Firestore `dynamicData/versioning` payload into latest entries per platform. */
export function parseLatestVersions(data: DocumentData | undefined): LatestVersions {
  if (!data) {
    return { web: null, ios: null, android: null }
  }

  const webArray = data.web as VersionEntry[] | undefined
  const iosArray = data.ios as VersionEntry[] | undefined
  const androidArray = data.android as VersionEntry[] | undefined

  const pickHighest = (entries: VersionEntry[] | undefined): VersionEntry | null => {
    if (!entries?.length) return null
    return entries.reduce((latest, current) =>
      isVersionOutdated(latest.version, current.version) ? current : latest
    )
  }

  return {
    web: pickHighest(webArray),
    ios: pickHighest(iosArray),
    android: pickHighest(androidArray),
  }
}

/** Compare running app versions against Firestore config (soft/hard, Later suppression). */
export function evaluateVersionCheck(
  deviceInfo: DeviceInfo,
  latestVersions: LatestVersions
): VersionCheckEvaluation {
  const isIosNative = deviceInfo.platform === 'ios-native'
  const isAndroidNative = deviceInfo.platform === 'android-native'

  const shouldShowWeb = Boolean(
    latestVersions.web &&
      latestVersions.web.level !== 'noNotification' &&
      deviceInfo.webVersion !== latestVersions.web.version
  )

  const shouldShowIos = Boolean(
    isIosNative &&
      deviceInfo.appVersion &&
      latestVersions.ios &&
      latestVersions.ios.level !== 'noNotification' &&
      deviceInfo.appVersion !== latestVersions.ios.version
  )

  const shouldShowAndroid = Boolean(
    isAndroidNative &&
      deviceInfo.appVersion &&
      latestVersions.android &&
      latestVersions.android.level !== 'noNotification' &&
      deviceInfo.appVersion !== latestVersions.android.version
  )

  const shouldShowNative = shouldShowIos || shouldShowAndroid

  let notificationType: VersionNotificationType = 'none'
  if (shouldShowWeb && shouldShowNative) {
    notificationType = 'both'
  } else if (shouldShowWeb) {
    notificationType = 'web'
  } else if (shouldShowIos) {
    notificationType = 'ios'
  } else if (shouldShowAndroid) {
    notificationType = 'android'
  }

  const isHard =
    (shouldShowWeb && latestVersions.web?.level === 'hardNotification') ||
    (shouldShowIos && latestVersions.ios?.level === 'hardNotification') ||
    (shouldShowAndroid && latestVersions.android?.level === 'hardNotification')

  const iosIsHard = shouldShowIos && latestVersions.ios?.level === 'hardNotification'
  const androidIsHard =
    shouldShowAndroid && latestVersions.android?.level === 'hardNotification'

  let shouldShow = false
  if (isHard) {
    shouldShow = Boolean(shouldShowWeb || shouldShowNative)
  } else {
    const laterClickedTimestamp = localStorage.getItem('versionCheck_laterClicked')
    if (laterClickedTimestamp) {
      const laterClickedTime = parseInt(laterClickedTimestamp, 10)
      const hoursSinceLater = (Date.now() - laterClickedTime) / (1000 * 60 * 60)
      if (hoursSinceLater >= 24) {
        localStorage.removeItem('versionCheck_laterClicked')
        shouldShow = Boolean(shouldShowWeb || shouldShowNative)
      }
    } else {
      shouldShow = Boolean(shouldShowWeb || shouldShowNative)
    }
  }

  const latestNativeVersion = shouldShowAndroid
    ? latestVersions.android?.version ?? null
    : shouldShowIos
      ? latestVersions.ios?.version ?? null
      : isAndroidNative
        ? latestVersions.android?.version ?? null
        : isIosNative
          ? latestVersions.ios?.version ?? null
          : null

  logVersionCheckEvaluation(deviceInfo, latestVersions, {
    shouldShow,
    isDismissable: !isHard,
    notificationType,
  })

  return {
    shouldShow,
    isDismissable: !isHard,
    notificationType,
    isHardNative: Boolean(iosIsHard || androidIsHard),
    currentWebVersion: deviceInfo.webVersion,
    latestWebVersion: latestVersions.web?.version ?? null,
    currentNativeVersion: deviceInfo.appVersion,
    latestNativeVersion,
  }
}

function logVersionCheckEvaluation(
  deviceInfo: DeviceInfo,
  latestVersions: LatestVersions,
  summary: { shouldShow: boolean; isDismissable: boolean; notificationType: VersionNotificationType }
) {
  console.log(
    '%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n' +
      '%c🔔 VERSION CHECK (snapshot)\n' +
      '%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
    'color: #3b82f6',
    'color: #f59e0b; font-weight: bold; font-size: 14px',
    'color: #3b82f6'
  )
  console.log(
    '%cNotification Type: %c' + summary.notificationType.toUpperCase(),
    'color: #94a3b8',
    summary.notificationType === 'none'
      ? 'color: #22c55e; font-weight: bold'
      : 'color: #f59e0b; font-weight: bold'
  )
  console.log(
    '%cShould Show: %c' + (summary.shouldShow ? 'YES' : 'NO'),
    'color: #94a3b8',
    summary.shouldShow ? 'color: #f59e0b; font-weight: bold' : 'color: #22c55e; font-weight: bold'
  )
  console.log(
    '%cDismissable: %c' + (summary.isDismissable ? 'YES ✓' : 'NO ✗ (HARD)'),
    'color: #94a3b8',
    summary.isDismissable ? 'color: #22c55e; font-weight: bold' : 'color: #ef4444; font-weight: bold'
  )
  console.log('%c\n📱 WEB VERSION', 'color: #06b6d4; font-weight: bold')
  console.log(
    '%c  Current:  %c' +
      deviceInfo.webVersion +
      '\n%c  Latest:   %c' +
      (latestVersions.web?.version || 'N/A') +
      '\n%c  Level:    %c' +
      (latestVersions.web?.level || 'N/A'),
    'color: #94a3b8',
    'color: #ffffff; font-weight: bold',
    'color: #94a3b8',
    'color: #ffffff; font-weight: bold',
    'color: #94a3b8',
    'color: #ffffff; font-weight: bold'
  )
  console.log(
    '%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n',
    'color: #3b82f6'
  )
}

/** One-shot read; live app uses {@link subscribeToVersioning}. */
export async function getLatestVersions(): Promise<LatestVersions> {
  try {
    const versioningDoc = await getDoc(getVersioningDocRef())
    if (!versioningDoc.exists()) {
      console.warn('[VersionCheck] Versioning document does not exist in Firestore')
      return { web: null, ios: null, android: null }
    }
    const latest = parseLatestVersions(versioningDoc.data())
    console.log('[VersionCheck] Fetched latest versions from Firestore:', latest)
    return latest
  } catch (error) {
    console.error('[VersionCheck] Error fetching latest versions from Firestore:', error)
    return { web: null, ios: null, android: null }
  }
}

/**
 * Real-time listener on `dynamicData/versioning` — fires on load and whenever the doc changes.
 */
export function subscribeToVersioning(
  onLatestVersions: (latest: LatestVersions) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  return onSnapshot(
    getVersioningDocRef(),
    (snapshot) => {
      const latest = parseLatestVersions(snapshot.exists() ? snapshot.data() : undefined)
      console.log('[VersionCheck] Versioning snapshot received:', latest)
      onLatestVersions(latest)
    },
    (error) => {
      console.error('[VersionCheck] Versioning snapshot error:', error)
      onError?.(error)
    }
  )
}

export function isVersionOutdated(currentVersion: string, latestVersion: string): boolean {
  try {
    if (currentVersion === 'unknown' || latestVersion === 'unknown') {
      return false
    }

    const current = currentVersion.split('.').map(Number)
    const latest = latestVersion.split('.').map(Number)

    for (let i = 0; i < Math.max(current.length, latest.length); i++) {
      const currentPart = current[i] || 0
      const latestPart = latest[i] || 0
      if (currentPart < latestPart) return true
      if (currentPart > latestPart) return false
    }

    return false
  } catch (error) {
    console.error('[VersionCheck] Error comparing versions:', error)
    return false
  }
}
